<template>
    <div class="p-4 border border-gray text-center h-full flex flex-col rounded relative overflow-hidden">
        <ribbon v-if="product.hot_buy">Featured</ribbon>
        <ribbon v-if="product.local" position="top_left">Local</ribbon>
        <div v-if="showFavouriteStar" class="absolute top-0 left-0 z-10">
            <BetterTooltip placement="bottom">
                <template #reference>
                    <span
                        v-if="favouritesLocked"
                        class="w-6 h-6 inline-flex items-center justify-center text-yellow-500"
                    >
                        <i class="fas fa-star text-xs" aria-hidden="true"></i>
                    </span>
                    <button
                        v-else
                        type="button"
                        class="favourite-star-btn w-6 h-6 inline-flex items-center justify-center rounded text-gray-400 hover:text-yellow-500 border-0 bg-transparent p-0"
                        :class="{ 'text-yellow-500': displayedFavourite }"
                        @click.prevent="toggleFavourite"
                    >
                        <i class="text-xs" :class="displayedFavourite ? 'fas fa-star' : 'far fa-star'" aria-hidden="true"></i>
                    </button>
                </template>
                <template #floating>
                    <div class="text-[11px]">
                        <template v-if="favouritesLocked">
                            <div class="whitespace-nowrap">Favourite</div>
                            <div>{{ removeFavouriteTooltipSuffix }}</div>
                        </template>
                        <template v-else-if="displayedFavourite">
                            <div class="whitespace-nowrap">Remove from Favourites</div>
                            <div>{{ removeFavouriteTooltipSuffix }}</div>
                        </template>
                        <template v-else><span class="whitespace-nowrap">Add to Favourites</span></template>
                    </div>
                </template>
            </BetterTooltip>
        </div>
        <div class="h-40 flex items-center justify-center product-image relative">
            <div class="overlay cursor-pointer"  @click="handleQuickview(product)">Click to view</div>

            <img v-if="product.image != null" 
                :src="'/storage/' + product.image" 
                class="mx-auto max-h-40" 
                alt="Product Image" 
                loading="lazy"
            />
            <img v-show="product.image == null" 
                src="/images/product-placeholder.png" 
                class="mx-auto max-h-40" 
                alt="No Image" 
                loading="lazy"
            />
        </div>

        <div class="flex-1">
            <h2
                class="leading-tight mt-3 mb-1 text-base md:text-lg md:leading-tight font-bold overflow-hidden cursor-pointer"
                @click="handleQuickview(product)"
            >
                {{ product.title }}
            </h2>
            <p class="leading-tight text-sm">{{ product.description }}</p>
            <p class="my-3">${{ displayPrice.toFixed(2) }} / {{ product.size }}</p>
            <p v-if="product.special_order" class="leading-tight text-xs italic font-bold text-red">{{ specialOrderLeadLabel }}</p>
        </div>

        <add-to-cart
            v-if="canPlaceOrders || isAdmin"
            :product="product"
            class="mt-3"
            @openSpecialOrderItemModal="openSpecialOrderItemModal()"
        />
    </div>

    <product-quickview
        :product="quickviewProduct"
        :show="showQuickview"
        :favourite-product-ids="favouriteProductIds"
        :show-favourite-star="showFavouriteStar"
        :customer-favourites-visible-only="customerFavouritesVisibleOnly"
        @close="showQuickview = false"
        @favourites-updated="$emit('favourites-updated', $event)"
    ></product-quickview>

    <jet-dialog-modal :show="showSpecialOrderItemModal" :closeable="true">
        <template #title>
            Special Order Item
        </template>

        <template #content>
            You've added a special order item to your cart. Please note this item will arrive {{ specialOrderArrivalDays }} after todays date.
        </template>

        <template #footer>
            <jet-tertiary-button @click="showSpecialOrderItemModal = false">
                Close
            </jet-tertiary-button>
        </template>
    </jet-dialog-modal>
</template>

<script>
import { computed } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3'
import { shouldApplySplitCaseSurcharge as productShouldApplySplitCaseSurcharge } from '@/Composables/UseProductQuantity';
import UseCustomerAccess from '@/Composables/UseCustomerAccess';
import UseUserAuth from '@/Composables/UseUserAuth';
import UseCart from '@/Composables/UseCart';
import AddToCart from '@/Pages/Catalogue/Partials/AddToCart';
import Ribbon from "@/Components/Ribbon";
import ProductQuickview from '@/Pages/Catalogue/Partials/ProductQuickview';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import BetterTooltip from '@/Jetstream/BetterTooltip';
import { specialOrderLeadTimeLabel } from '@/helper';

export default {
    props: ['product'],

    setup (props) {
        const { isAdmin } = UseUserAuth(usePage().props.value.user);
        const { canPlaceOrders } = UseCustomerAccess();
        const { cart } = UseCart(usePage().props.value.user, usePage().props.value.activeCustomer ? usePage().props.value.activeCustomer.tax_exempt : 0);

        const cartQuantity = computed(() => {
            const item = cart.value.find(i => i.product && i.product.id === props.product.id);
            return item ? item.quantity : 0;
        });
        const displayPrice = computed(() => {
            const q = cartQuantity.value;
            const activeCustomer = usePage().props.value.activeCustomer;
            if (productShouldApplySplitCaseSurcharge(q, activeCustomer, props.product) && props.product.split_case_price != null) return props.product.split_case_price;
            return props.product.price != null ? props.product.price : 0;
        });

        return {
            isAdmin,
            canPlaceOrders,
            cartQuantity,
            displayPrice,
        };
    },

    data () {
        return {
            quickviewProduct: null,
            showQuickview: false,
            showSpecialOrderItemModal: false,
        }
    },

    components: {
        Ribbon,
        AddToCart,
        ProductQuickview,
        JetDialogModal,
        JetTertiaryButton,
        BetterTooltip,
    },

    methods: {
        shouldApplySplitCaseSurcharge (quantity) {
            return productShouldApplySplitCaseSurcharge(quantity, this.$page.props.activeCustomer, this.product);
        },
        handleQuickview (product) {
            this.quickviewProduct = product;
            this.showQuickview = true;
        },

        openSpecialOrderItemModal () {
            if (!this.showQuickview)
            {
                this.showSpecialOrderItemModal = true;
            }
        },

        toggleFavourite () {
            // favouriteToggling locks the toggle; only one request can fire at a time
            if (this.favouriteToggling || this.favouritesLocked) return;
            this.favouriteToggling = true;
            // displayFav is used to "optimistically" update the UI immediately before the request is made
            // user doesn't have to wait for the request to complete to see the icon update
            this.displayFav = !this.displayedFavourite;
            const request = this.displayFav
                ? axios.post(route('catalogue.favourites.store'), { product_id: this.product.id })
                : axios.delete(route('catalogue.favourites.destroy', this.product.id));
            request
                .then(({ data }) => {
                    if (data.success && data.favouriteProductIds) {
                        this.$emit('favourites-updated', {
                            favouriteProductIds: data.favouriteProductIds,
                            favouriteDetails: data.favouriteDetails || {},
                        });
                    }
                    this.displayFav = null;
                })
                .catch(() => {
                    this.displayFav = null;
                })
                .finally(() => {
                    this.favouriteToggling = false;
                });
        },

        formatFavouriteDate (isoString) {
            if (!isoString) return '';
            const d = new Date(isoString);
            return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        },
    },

    props: {
        product: { type: Object, required: true },
        favouriteProductIds: { type: Array, default: () => [] },
        favouriteDetails: { type: Object, default: () => ({}) },
        showFavouriteStar: { type: Boolean, default: false },
        customerFavouritesVisibleOnly: { type: Boolean, default: false },
    },

    data () {
        return {
            quickviewProduct: null,
            showQuickview: false,
            showSpecialOrderItemModal: false,
            favouriteToggling: false,
            displayFav: null,
        };
    },

    computed: {
        specialOrderLeadLabel () {
            return specialOrderLeadTimeLabel(this.product.special_order_lead_hours);
        },
        specialOrderArrivalDays () {
            const hours = Number(this.product.special_order_lead_hours);
            if (hours === 24) return '1 shipping day';
            if (hours === 72) return '3 shipping days';
            return '2 shipping days';
        },
        isFavourite () {
            return this.showFavouriteStar && this.favouriteProductIds.includes(this.product.id);
        },
        displayedFavourite () {
            return this.displayFav !== null ? this.displayFav : this.isFavourite;
        },
        favouritesLocked () {
            return this.customerFavouritesVisibleOnly && this.displayedFavourite;
        },
        removeFavouriteTooltipSuffix () {
            const detail = this.favouriteDetails && this.favouriteDetails[this.product.id];
            return !detail
                ? 'Added by a previous order'
                : (detail.created_by_user_name
                    ? `Added by ${detail.created_by_user_name} on ${this.formatFavouriteDate(detail.created_at)}`
                    : 'Added by a previous order');
        },
    },
}
</script>

<style>

    .favourite-star-btn:focus {
        outline: none;
        box-shadow: none;
    }

    .product-image:hover img {
        opacity: 0.5;
    }

    .product-image:hover .overlay {
        display: block;
    }

    .overlay {
        display: none;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        padding: 3.95rem 0;
        font-weight: bold;
        text-shadow: 0px 1px 3px #FFF;
        color: #555;
        z-index: 8;
    }

</style>
